import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import axios from 'axios';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { jwtDecode } from 'jwt-decode';

// Environment variables with fallbacks
const AUTH_TOKEN_NAME = process.env.REACT_APP_AUTH_TOKEN_NAME || 'authKey';
const CSRF_TOKEN_NAME = process.env.REACT_APP_CSRF_TOKEN_NAME || 'securityKey';
// The app talks to the API via the relative '/api' base (CRA proxy in dev,
// Firebase Hosting rewrite in prod) — diagnostics must test the same origin.
const API_URL = '/api';

const DiagnosticReport = () => {
  const { user, isAuthenticated } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [diagnosticResults, setDiagnosticResults] = useState({
    environment: {},
    tokens: {},
    cookies: {},
    corsTest: {},
    csrfTest: {},
    authTest: {},
  });

  useEffect(() => {
    runDiagnostics();
  }, []);

  const runDiagnostics = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      // Environment check
      const environment = {
        browserInfo: {
          userAgent: navigator.userAgent,
          cookiesEnabled: navigator.cookieEnabled,
        },
        appConfig: {
          apiUrl: process.env.REACT_APP_API_URL || 'Not set',
          withCredentials: process.env.REACT_APP_WITH_CREDENTIALS || 'Not set',
          nodeEnv: process.env.NODE_ENV,
          authTokenName: AUTH_TOKEN_NAME,
          csrfTokenName: CSRF_TOKEN_NAME,
          buildDate: process.env.REACT_APP_BUILD_DATE || 'Unknown',
        },
        origins: {
          currentOrigin: window.location.origin,
          targetApi: API_URL,
          isSameOrigin: window.location.origin === API_URL,
        }
      };
      
      // Token check
      const tokens = {
        auth: {
          exists: !!localStorage.getItem(AUTH_TOKEN_NAME),
          value: localStorage.getItem(AUTH_TOKEN_NAME) ? 'Present (hidden for security)' : 'Not present',
        },
        csrf: {
          exists: !!localStorage.getItem(CSRF_TOKEN_NAME),
          value: localStorage.getItem(CSRF_TOKEN_NAME) ? 'Present (hidden for security)' : 'Not present',
        }
      };
      
      // Try to decode JWT if it exists
      if (tokens.auth.exists) {
        try {
          const decodedToken = jwtDecode(localStorage.getItem(AUTH_TOKEN_NAME));
          const currentTime = Date.now() / 1000;
          tokens.auth.decoded = {
            exp: decodedToken.exp ? new Date(decodedToken.exp * 1000).toISOString() : 'No expiration',
            isExpired: decodedToken.exp ? (decodedToken.exp < currentTime) : 'Unknown',
            iat: decodedToken.iat ? new Date(decodedToken.iat * 1000).toISOString() : 'No issued date',
            userId: decodedToken.id || decodedToken.userId || 'User ID not found in token',
          };
        } catch (error) {
          tokens.auth.decodingError = error.message;
        }
      }
      
      // Cookie check
      const cookies = {
        hasCookies: !!document.cookie,
        count: document.cookie ? document.cookie.split(';').length : 0,
        names: document.cookie 
          ? document.cookie.split(';').map(c => c.trim().split('=')[0]) 
          : [],
      };
      
      // CORS test - basic OPTIONS preflight
      let corsTest = { success: false, error: null, details: null };
      try {
        console.log('Running CORS preflight test...');
        const corsResponse = await axios({
          url: `${API_URL}/auth/csrf-token`,
          method: 'OPTIONS',
          headers: {
            'Access-Control-Request-Method': 'GET',
            'Access-Control-Request-Headers': 'content-type',
            'Origin': window.location.origin
          }
        });
        
        corsTest = {
          success: true,
          status: corsResponse.status,
          statusText: corsResponse.statusText,
          headers: {
            'access-control-allow-origin': corsResponse.headers['access-control-allow-origin'],
            'access-control-allow-methods': corsResponse.headers['access-control-allow-methods'],
            'access-control-allow-headers': corsResponse.headers['access-control-allow-headers'],
            'access-control-allow-credentials': corsResponse.headers['access-control-allow-credentials'],
          }
        };
      } catch (error) {
        corsTest = { 
          success: false, 
          error: error.message,
          details: error.response ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers
          } : 'No response received',
          isCORSError: error.message.includes('Network Error')
        };
      }
      
      // CSRF token test
      let csrfTest = { success: false, error: null, details: null };
      try {
        console.log('Running CSRF token test...');
        const csrfResponse = await api.get('/auth/csrf-token');
        
        const newToken = csrfResponse.data.csrfToken;
        const csrfStorageBefore = localStorage.getItem(CSRF_TOKEN_NAME);
        
        csrfTest = {
          success: true,
          tokenReceived: !!newToken,
          tokenLength: newToken ? newToken.length : 0,
          storageBeforeRequest: csrfStorageBefore ? 'Present' : 'Not present',
          storageAfterRequest: localStorage.getItem(CSRF_TOKEN_NAME) ? 'Present' : 'Not present',
          tokenChanged: csrfStorageBefore !== localStorage.getItem(CSRF_TOKEN_NAME),
          headers: {
            'content-type': csrfResponse.headers['content-type'],
            'set-cookie': csrfResponse.headers['set-cookie'] ? 'Present' : 'Not present',
            'access-control-allow-origin': csrfResponse.headers['access-control-allow-origin'],
            'access-control-allow-credentials': csrfResponse.headers['access-control-allow-credentials'],
          }
        };
      } catch (error) {
        csrfTest = { 
          success: false, 
          error: error.message,
          details: error.response ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers
          } : 'No response received',
          isCORSError: error.message.includes('Network Error')
        };
      }
      
      // Auth test - try to get user profile if authenticated
      let authTest = { success: false, error: null, details: null };
      if (isAuthenticated) {
        try {
          console.log('Running auth test...');
          const authResponse = await api.get('/auth/me');
          
          authTest = {
            success: true,
            userReceived: !!authResponse.data.data,
            userId: authResponse.data.data ? authResponse.data.data.id : 'Not available',
            headers: {
              'content-type': authResponse.headers['content-type'],
              'set-cookie': authResponse.headers['set-cookie'] ? 'Present' : 'Not present',
              'access-control-allow-origin': authResponse.headers['access-control-allow-origin'],
              'access-control-allow-credentials': authResponse.headers['access-control-allow-credentials'],
            }
          };
        } catch (error) {
          authTest = { 
            success: false, 
            error: error.message,
            details: error.response ? {
              status: error.response.status,
              data: error.response.data,
              headers: error.response.headers
            } : 'No response received',
            isCORSError: error.message.includes('Network Error')
          };
        }
      } else {
        authTest = {
          skipped: true,
          reason: 'User not authenticated'
        };
      }
      
      setDiagnosticResults({
        environment,
        tokens,
        cookies,
        corsTest,
        csrfTest,
        authTest
      });
    } catch (err) {
      setError(`Failed to run diagnostics: ${err.message}`);
      console.error('Diagnostic error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper to render test status
  const renderTestStatus = (test) => {
    if (test.skipped) {
      return <Chip icon={<WarningIcon />} label="Skipped" color="warning" />;
    }
    
    return test.success 
      ? <Chip icon={<CheckCircleIcon />} label="Success" color="success" /> 
      : <Chip icon={<ErrorIcon />} label="Failed" color="error" />;
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" component="h1">
            System Diagnostics
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={runDiagnostics}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isLoading ? 'Running...' : 'Run Diagnostics'}
          </Button>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Typography variant="body2" color="text.secondary" paragraph>
          This page runs tests on your application configuration, network connectivity, CORS settings, and authentication to help diagnose issues.
        </Typography>
        
        <Divider sx={{ my: 3 }} />
        
        {/* Environment Information */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Environment Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="Browser" 
                  secondary={diagnosticResults.environment.browserInfo?.userAgent || 'Not available'} 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Cookies Enabled" 
                  secondary={diagnosticResults.environment.browserInfo?.cookiesEnabled ? 'Yes' : 'No'} 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="React Environment" 
                  secondary={diagnosticResults.environment.appConfig?.nodeEnv || 'Not available'} 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="API URL" 
                  secondary={diagnosticResults.environment.appConfig?.apiUrl || 'Not available'} 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="With Credentials Setting" 
                  secondary={diagnosticResults.environment.appConfig?.withCredentials || 'Not available'} 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Current Origin" 
                  secondary={diagnosticResults.environment.origins?.currentOrigin || 'Not available'} 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Target API Origin" 
                  secondary={(() => {
                    const apiUrl = diagnosticResults.environment.origins?.targetApi;
                    if (!apiUrl) return 'Not available';
                    try {
                      return new URL(apiUrl).origin;
                    } catch {
                      return apiUrl + ' (cannot parse as URL)';
                    }
                  })()} 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Same Origin Status" 
                  secondary={(() => {
                    const isSameOrigin = diagnosticResults.environment.origins?.isSameOrigin;
                    return isSameOrigin === true ? 'Same origin' : isSameOrigin === false ? 'Cross-origin' : 'Unknown';
                  })()} 
                />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>
        
        {/* Tokens Information */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Tokens</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="subtitle1" gutterBottom>Auth Token</Typography>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="Auth Token Exists" 
                  secondary={diagnosticResults.tokens.auth?.exists ? 'Yes' : 'No'} 
                />
              </ListItem>
              {diagnosticResults.tokens.auth?.decoded && (
                <>
                  <ListItem>
                    <ListItemText 
                      primary="Token Expiration" 
                      secondary={diagnosticResults.tokens.auth.decoded.exp} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Token Status" 
                      secondary={diagnosticResults.tokens.auth.decoded.isExpired === true ? 'Expired' : 'Valid'} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Token Issue Date" 
                      secondary={diagnosticResults.tokens.auth.decoded.iat} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="User ID in Token" 
                      secondary={diagnosticResults.tokens.auth.decoded.userId} 
                    />
                  </ListItem>
                </>
              )}
              {diagnosticResults.tokens.auth?.decodingError && (
                <ListItem>
                  <ListItemText 
                    primary="Token Decoding Error" 
                    secondary={diagnosticResults.tokens.auth.decodingError} 
                  />
                </ListItem>
              )}
            </List>
            
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>CSRF Token</Typography>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="CSRF Token Exists" 
                  secondary={diagnosticResults.tokens.csrf?.exists ? 'Yes' : 'No'} 
                />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>
        
        {/* Cookies Information */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Cookies</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="Has Cookies" 
                  secondary={diagnosticResults.cookies?.hasCookies ? 'Yes' : 'No'} 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Cookie Count" 
                  secondary={diagnosticResults.cookies?.count || 0} 
                />
              </ListItem>
              {diagnosticResults.cookies?.hasCookies && (
                <ListItem>
                  <ListItemText 
                    primary="Cookie Names" 
                    secondary={diagnosticResults.cookies?.names?.join(', ') || 'None'} 
                  />
                </ListItem>
              )}
            </List>
          </AccordionDetails>
        </Accordion>
        
        {/* CORS Test */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
              <Typography variant="h6">CORS Test</Typography>
              {diagnosticResults.corsTest && renderTestStatus(diagnosticResults.corsTest)}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {diagnosticResults.corsTest?.success ? (
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Status" 
                    secondary={`${diagnosticResults.corsTest.status} ${diagnosticResults.corsTest.statusText}`} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Allow Origin" 
                    secondary={diagnosticResults.corsTest.headers?.['access-control-allow-origin'] || 'Not set'} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Allow Methods" 
                    secondary={diagnosticResults.corsTest.headers?.['access-control-allow-methods'] || 'Not set'} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Allow Headers" 
                    secondary={diagnosticResults.corsTest.headers?.['access-control-allow-headers'] || 'Not set'} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Allow Credentials" 
                    secondary={diagnosticResults.corsTest.headers?.['access-control-allow-credentials'] || 'Not set'} 
                  />
                </ListItem>
              </List>
            ) : (
              <Box>
                <Alert severity="error" sx={{ mb: 2 }}>
                  CORS Test Failed: {diagnosticResults.corsTest?.error || 'Unknown error'}
                </Alert>
                
                {diagnosticResults.corsTest?.isCORSError && (
                  <Alert severity="info">
                    This appears to be a CORS-related error. Check that your backend is correctly configured
                    to allow requests from {window.location.origin}.
                  </Alert>
                )}
                
                {diagnosticResults.corsTest?.details && (
                  <Typography variant="body2" component="pre" sx={{ mt: 2, bgcolor: 'action.hover', p: 2, borderRadius: '10px', overflowX: 'auto', fontSize: '0.75rem' }}>
                    {JSON.stringify(diagnosticResults.corsTest.details, null, 2)}
                  </Typography>
                )}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
        
        {/* CSRF Token Test */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
              <Typography variant="h6">CSRF Token Test</Typography>
              {diagnosticResults.csrfTest && renderTestStatus(diagnosticResults.csrfTest)}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {diagnosticResults.csrfTest?.success ? (
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Token Received" 
                    secondary={diagnosticResults.csrfTest.tokenReceived ? 'Yes' : 'No'} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Token Length" 
                    secondary={diagnosticResults.csrfTest.tokenLength || 0} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Storage Before Request" 
                    secondary={diagnosticResults.csrfTest.storageBeforeRequest} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Storage After Request" 
                    secondary={diagnosticResults.csrfTest.storageAfterRequest} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Token Changed" 
                    secondary={diagnosticResults.csrfTest.tokenChanged ? 'Yes' : 'No'} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Set-Cookie Header" 
                    secondary={diagnosticResults.csrfTest.headers?.['set-cookie'] || 'Not set'} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Allow Origin" 
                    secondary={diagnosticResults.csrfTest.headers?.['access-control-allow-origin'] || 'Not set'} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Allow Credentials" 
                    secondary={diagnosticResults.csrfTest.headers?.['access-control-allow-credentials'] || 'Not set'} 
                  />
                </ListItem>
              </List>
            ) : (
              <Box>
                <Alert severity="error" sx={{ mb: 2 }}>
                  CSRF Test Failed: {diagnosticResults.csrfTest?.error || 'Unknown error'}
                </Alert>
                
                {diagnosticResults.csrfTest?.isCORSError && (
                  <Alert severity="info">
                    This appears to be a CORS-related error. Check that your backend is correctly configured
                    to allow requests from {window.location.origin}.
                  </Alert>
                )}
                
                {diagnosticResults.csrfTest?.details && (
                  <Typography variant="body2" component="pre" sx={{ mt: 2, bgcolor: 'action.hover', p: 2, borderRadius: '10px', overflowX: 'auto', fontSize: '0.75rem' }}>
                    {JSON.stringify(diagnosticResults.csrfTest.details, null, 2)}
                  </Typography>
                )}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
        
        {/* Authentication Test */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
              <Typography variant="h6">Authentication Test</Typography>
              {diagnosticResults.authTest && renderTestStatus(diagnosticResults.authTest)}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {diagnosticResults.authTest?.skipped ? (
              <Alert severity="warning">
                Authentication test skipped: {diagnosticResults.authTest.reason}
              </Alert>
            ) : diagnosticResults.authTest?.success ? (
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="User Data Received" 
                    secondary={diagnosticResults.authTest.userReceived ? 'Yes' : 'No'} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="User ID" 
                    secondary={diagnosticResults.authTest.userId} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Set-Cookie Header" 
                    secondary={diagnosticResults.authTest.headers?.['set-cookie'] || 'Not set'} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Allow Origin" 
                    secondary={diagnosticResults.authTest.headers?.['access-control-allow-origin'] || 'Not set'} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Allow Credentials" 
                    secondary={diagnosticResults.authTest.headers?.['access-control-allow-credentials'] || 'Not set'} 
                  />
                </ListItem>
              </List>
            ) : (
              <Box>
                <Alert severity="error" sx={{ mb: 2 }}>
                  Authentication Test Failed: {diagnosticResults.authTest?.error || 'Unknown error'}
                </Alert>
                
                {diagnosticResults.authTest?.isCORSError && (
                  <Alert severity="info">
                    This appears to be a CORS-related error. Check that your backend is correctly configured
                    to allow requests from {window.location.origin}.
                  </Alert>
                )}
                
                {diagnosticResults.authTest?.details && (
                  <Typography variant="body2" component="pre" sx={{ mt: 2, bgcolor: 'action.hover', p: 2, borderRadius: '10px', overflowX: 'auto', fontSize: '0.75rem' }}>
                    {JSON.stringify(diagnosticResults.authTest.details, null, 2)}
                  </Typography>
                )}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </Paper>
    </Container>
  );
};

export default DiagnosticReport;